import { useState } from "react";
import {
  ADMIN_CLOSE_WORK_ITEM_LABEL,
  type AdminClosableWorkItemKind,
} from "@care-by-ella/contracts";
import { UnauthorizedError } from "@/lib/clinician-api";
import { errorText } from "@/lib/error-text";
import { adminCloseWorkItem } from "@/lib/work-queue-api";
import { useIsAdmin } from "./CoachAssignment";

const BTN =
  "rounded-md border border-surfaceMuted px-md py-sm text-sm text-textSecondary hover:text-textPrimary disabled:opacity-50";
const BTN_PRIMARY =
  "rounded-md bg-brand px-md py-sm text-sm text-white hover:bg-brand/90 disabled:opacity-50";
const FIELD =
  "min-w-[16rem] rounded-md border border-surfaceMuted bg-surface px-sm py-xs text-sm text-textPrimary focus:border-brand focus:outline-none";

// TES-1580 — proposed copy, for Emelie to confirm (TES-475).
const WEBDOC_CLOSE_HINT =
  "Anteckningen markeras inte som införd i Webdoc. Orsaken sparas med ditt namn och tidpunkten.";

/**
 * TES-1400 (Ö-410) — "Stäng arbetsmoment": an admin closes an
 * administrative work item with a reason. Renders nothing for anyone but an
 * admin, and the caller only mounts it on an administrative kind; the server
 * refuses every other kind regardless. Closing changes nothing medical: it
 * takes the row out of the queue and keeps the reason in its history.
 */
export function AdminCloseWorkItem({
  kind,
  itemId,
  onClosed,
  onUnauthorized,
}: {
  kind: AdminClosableWorkItemKind;
  itemId: string;
  onClosed: () => void;
  onUnauthorized?: () => void;
}) {
  const admin = useIsAdmin();
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!admin) return null;

  if (!asking) {
    return (
      <button type="button" className={BTN} onClick={() => setAsking(true)}>
        {ADMIN_CLOSE_WORK_ITEM_LABEL}
      </button>
    );
  }

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await adminCloseWorkItem(kind, itemId, { reason: reason.trim() });
      onClosed();
    } catch (e) {
      if (e instanceof UnauthorizedError) onUnauthorized?.();
      else setError(e instanceof Error ? errorText(e.message) : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-sm">
      {/* TES-1580 — closing the Webdoc row is the one way past the reminder
          without a tick, so say so where the reason is asked for. */}
      {kind === "webdoc_confirmation" ? (
        <p className="w-full text-sm text-textSecondary">{WEBDOC_CLOSE_HINT}</p>
      ) : null}
      <input
        aria-label="Orsak"
        placeholder="Varför stängs arbetsmomentet?"
        className={FIELD}
        value={reason}
        maxLength={2000}
        onChange={(e) => setReason(e.target.value)}
      />
      <button
        type="button"
        className={BTN_PRIMARY}
        disabled={busy || reason.trim() === ""}
        onClick={() => void submit()}
      >
        {ADMIN_CLOSE_WORK_ITEM_LABEL}
      </button>
      <button
        type="button"
        className={BTN}
        onClick={() => {
          setAsking(false);
          setError(null);
        }}
      >
        Avbryt
      </button>
      {error ? <span className="text-sm text-danger">{error}</span> : null}
    </div>
  );
}
